'use client';
import React, { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ModalProps {
  open?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
}

export default function Modal({ open, isOpen, onClose, title, children, size = 'md', footer }: ModalProps) {
  const isVisible = open ?? isOpen ?? false;

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isVisible]);

  if (!isVisible) return null;

  const sizeClass = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div
        className="absolute inset-0 bg-foreground/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative bg-card w-full ${sizeClass} flex flex-col
          rounded-t-2xl sm:rounded-xl shadow-modal
          max-h-[92vh] sm:max-h-[90vh]
          animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200`}
      >
        {/* Drag handle for mobile */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border flex-shrink-0">
          <h3 className="text-base sm:text-lg font-600 text-foreground pr-2">{title}</h3>
          <button
            onClick={onClose}
            className="p-2.5 sm:p-1.5 rounded-xl sm:rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 overscroll-contain">
          {children}
        </div>
        {footer && (
          <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-border bg-muted/30 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}